import type { FC } from "react";

import type {
  DashboardExtensivePlan,
  DashboardExtensivePlanUnitOfWork,
  DashboardPlanSnapshot,
  DashboardPlanSnapshotNode,
} from "../../dashboard-client/types.gen";
import type { PlanRevisionTelemetry, TaskSnapshot } from "../../types";

import { formatUsd } from "../../utils/currency";
import { formatDurationMs, formatRelativeTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { ContentCard } from "../common/ContentCard";
import { SectionHeader } from "../common/SectionHeader";
import { StatusBadge } from "../common/StatusBadge";

// ---- Helpers ----

function deriveFlowStatus(tasks: TaskSnapshot[]): string {
  if (tasks.length === 0) {
    return "pending";
  }
  if (tasks.some((t) => t.status === "running")) {
    return "running";
  }
  if (tasks.every((t) => t.status === "succeeded")) {
    return "succeeded";
  }
  if (tasks.some((t) => t.status === "failed" || t.status === "cancelled")) {
    return "failed";
  }
  if (tasks.some((t) => t.status === "queued")) {
    return "queued";
  }
  return tasks[0].status;
}

function totalCost(tasks: TaskSnapshot[]): number {
  return tasks.reduce((sum, t) => sum + (t.cost_usd ?? 0), 0);
}

function uniqueNodes(tasks: TaskSnapshot[]): string[] {
  const nodes = new Set<string>();
  for (const t of tasks) {
    if (t.assigned_node) {
      nodes.add(t.assigned_node);
    }
  }
  return [...nodes];
}

function flowDurationMs(tasks: TaskSnapshot[]): number | null {
  const timestamps = tasks
    .flatMap((t) => [t.created_at, t.updated_at])
    .filter((v): v is number => typeof v === "number" && v > 0);
  if (timestamps.length < 2) {
    return null;
  }
  const min = Math.min(...timestamps);
  const max = Math.max(...timestamps);
  const diff = max - min;
  return diff > 0 ? diff : null;
}

// ---- Inline icons ----

const ClockIcon: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const IntentIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const CommentIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
    />
  </svg>
);

// ---- Metric cell ----

const MetricCell: FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
      {label}
    </span>
    <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-100">
      {value}
    </span>
    {sub && (
      <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
        {sub}
      </span>
    )}
  </div>
);

// ---- Main component ----

export interface PlanRevisionFlowSummaryProps {
  tasks: TaskSnapshot[];
  telemetry: PlanRevisionTelemetry | null;
  telemetryStatus: "idle" | "loading" | "loaded" | "error";
  planSnapshot: DashboardPlanSnapshot | null;
}

export const PlanRevisionFlowSummary: FC<PlanRevisionFlowSummaryProps> = ({
  tasks,
  telemetry,
  telemetryStatus,
  planSnapshot,
}) => {
  const status = deriveFlowStatus(tasks);
  const cost = totalCost(tasks);
  const nodes = uniqueNodes(tasks);
  const duration = flowDurationMs(tasks);
  const latestUpdatedAt =
    tasks.length > 0 ? Math.max(...tasks.map((t) => t.updated_at)) : null;

  const intent = telemetry?.intent;
  const commentContext = telemetry?.comment_context;
  const failure = telemetry?.failure;
  const snapshotNodes = planSnapshot?.nodes ?? [];
  const subtaskNodes = snapshotNodes.filter((n) => !n.is_story);
  const touchpointTotal = telemetry?.touchpoints?.touchpoints.length ?? 0;

  return (
    <div className="space-y-4">
      {/* Status bar */}
      <ContentCard padding="sm">
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={status} size="md" />

          {latestUpdatedAt && (
            <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <ClockIcon />
              {formatRelativeTime(latestUpdatedAt)}
            </span>
          )}

          {duration !== null && (
            <Badge variant="default" size="sm">
              {formatDurationMs(duration)}
            </Badge>
          )}

          {intent?.scope && (
            <Badge variant="info" size="sm">
              {intent.scope.replace("_", " ")}
            </Badge>
          )}

          {intent?.confidence != null && (
            <Badge
              variant={intent.confidence >= 0.8 ? "success" : "warning"}
              size="sm"
            >
              {Math.round(intent.confidence * 100)}% conf
            </Badge>
          )}
        </div>
      </ContentCard>

      {/* Metric cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ContentCard padding="sm">
          <MetricCell
            label="Cost"
            value={cost > 0 ? formatUsd(cost) : "—"}
            sub={tasks.length > 1 ? `${tasks.length} tasks` : undefined}
          />
        </ContentCard>

        <ContentCard padding="sm">
          <MetricCell
            label="Nodes"
            value={nodes.length > 0 ? nodes.join(", ") : "—"}
          />
        </ContentCard>

        <ContentCard padding="sm">
          <MetricCell
            label="Subtasks"
            value={String(subtaskNodes.length)}
            sub={
              touchpointTotal > 0 ? `${touchpointTotal} touchpoints` : undefined
            }
          />
        </ContentCard>

        <ContentCard padding="sm">
          <MetricCell
            label="Conflicts"
            value={
              telemetryStatus === "loaded"
                ? String(telemetry?.snapshot_conflict_count ?? 0)
                : "—"
            }
          />
        </ContentCard>
      </div>

      {/* Failure alert */}
      {failure && (
        <Alert variant="error">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em]">
            {failure.stage ? `Failed at ${failure.stage}` : "Failed"}
          </p>
          <p className="mt-1 text-xs">{failure.reason}</p>
        </Alert>
      )}

      {/* Intent */}
      {intent?.intent && (
        <div className="space-y-1.5">
          <SectionHeader title="Intent" icon={<IntentIcon />} />
          <ContentCard padding="sm">
            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              {intent.intent}
            </p>
            {intent.notes && (
              <p className="mt-2 border-t border-slate-200 pt-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {intent.notes}
              </p>
            )}
          </ContentCard>
        </div>
      )}

      {/* Comment context */}
      {commentContext?.summary && (
        <div className="space-y-1.5">
          <SectionHeader title="Triggered By" icon={<CommentIcon />} />
          <ContentCard padding="sm">
            <p className="text-sm italic text-slate-600 dark:text-slate-400">
              {commentContext.summary}
            </p>
          </ContentCard>
        </div>
      )}

      {/* Produced output — plan snapshot */}
      {planSnapshot && <SnapshotOutput snapshot={planSnapshot} />}

      {/* Tracker summary */}
      {telemetry?.tracker && <TrackerSummary tracker={telemetry.tracker} />}
    </div>
  );
};

// ---- Snapshot output ----

const OutputIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
    />
  </svg>
);

const EFFORT_LABEL: Record<string, string> = {
  l: "L",
  m: "M",
  s: "S",
  xl: "XL",
  xs: "XS",
};

const RISK_VARIANT: Record<string, "success" | "warning" | "danger"> = {
  high: "danger",
  low: "success",
  medium: "warning",
};

const SnapshotOutput: FC<{ snapshot: DashboardPlanSnapshot }> = ({
  snapshot,
}) => {
  const plan = snapshot.uow_plan;
  const subtaskNodes = (snapshot.nodes ?? []).filter((n) => !n.is_story);

  return (
    <div className="space-y-4">
      {/* Plan overview */}
      {plan && <PlanOverview plan={plan} />}

      {/* Subtask nodes */}
      {subtaskNodes.length > 0 && (
        <div className="space-y-1.5">
          <SectionHeader
            title="Subtasks"
            icon={<OutputIcon />}
            badge={String(subtaskNodes.length)}
          />
          <ContentCard padding="none">
            <div className="divide-y divide-slate-200 dark:divide-slate-700/50">
              {subtaskNodes.map((node) => (
                <NodeRow key={node.node_id} node={node} />
              ))}
            </div>
          </ContentCard>
        </div>
      )}

      {/* Units of work */}
      {plan && plan.units_of_work.length > 0 && (
        <div className="space-y-1.5">
          <SectionHeader
            title="Units of Work"
            badge={String(plan.units_of_work.length)}
          />
          <ContentCard padding="none">
            <div className="divide-y divide-slate-200 dark:divide-slate-700/50">
              {plan.units_of_work.map((uow) => (
                <UowRow key={uow.unit_of_work_id} uow={uow} />
              ))}
            </div>
          </ContentCard>
        </div>
      )}
    </div>
  );
};

// ---- Plan overview ----

function complexityVariant(
  score: DashboardExtensivePlan["plan_complexity_score"],
): "success" | "warning" | "danger" {
  switch (score) {
    case "high": {
      return "danger";
    }
    case "medium": {
      return "warning";
    }
    case "low": {
      return "success";
    }
  }
}

const PlanOverview: FC<{ plan: DashboardExtensivePlan }> = ({ plan }) => (
  <div className="space-y-1.5">
    <SectionHeader title="Plan Overview" />
    <ContentCard padding="sm">
      <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
        {plan.overview}
      </p>

      {/* Metadata row */}
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2 dark:border-slate-700">
        <Badge
          variant={complexityVariant(plan.plan_complexity_score)}
          size="sm"
        >
          {plan.plan_complexity_score} complexity
        </Badge>

        {plan.repository_context_primary.map((repo) => (
          <Badge key={repo} variant="default" size="sm">
            {repo}
          </Badge>
        ))}
      </div>

      {/* Integration notes */}
      {plan.integration_notes.length > 0 && (
        <div className="mt-2 border-t border-slate-200 pt-2 dark:border-slate-700">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            Integration Notes
          </span>
          <ul className="mt-1 space-y-0.5">
            {plan.integration_notes.map((note) => (
              <li
                key={note}
                className="text-xs leading-relaxed text-slate-500 dark:text-slate-400"
              >
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </ContentCard>
  </div>
);

// ---- Node row (subtask) ----

const NodeRow: FC<{ node: DashboardPlanSnapshotNode }> = ({ node }) => (
  <div className="flex items-start gap-3 px-3 py-2.5">
    <span className="flex-shrink-0 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-purple-600 dark:text-purple-400">
      {node.issue_key}
    </span>

    <div className="min-w-0 flex-1">
      <p className="truncate text-sm text-slate-800 dark:text-slate-200">
        {node.title ?? node.node_id}
      </p>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        {node.repository_slug && (
          <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
            {node.repository_slug}
          </span>
        )}
        {node.unit_of_work_ids.length > 0 && (
          <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
            {node.unit_of_work_ids.length} UOW
            {node.unit_of_work_ids.length !== 1 ? "s" : ""}
          </span>
        )}
        {node.dependencies.length > 0 && (
          <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
            {node.dependencies.length} dep
            {node.dependencies.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>

    <div className="flex flex-shrink-0 items-center gap-1.5">
      {node.size_symbol && (
        <Badge variant="default" size="sm">
          {node.size_symbol}
        </Badge>
      )}
      {node.self_assessed_risk && (
        <Badge
          variant={RISK_VARIANT[node.self_assessed_risk] ?? "default"}
          size="sm"
        >
          {node.self_assessed_risk}
        </Badge>
      )}
    </div>
  </div>
);

// ---- UOW row ----

const DependencyArrowIcon: FC = () => (
  <svg
    className="h-2.5 w-2.5 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const UowRow: FC<{ uow: DashboardExtensivePlanUnitOfWork }> = ({ uow }) => (
  <div className="px-3 py-2.5">
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-800 dark:text-slate-200">
          {uow.title}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
            {uow.unit_of_work_id}
          </span>
          <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
            {uow.primary_repository_slug}
          </span>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-1.5">
        {uow.estimated_effort && (
          <Badge variant="default" size="sm">
            {EFFORT_LABEL[uow.estimated_effort] ?? uow.estimated_effort}
          </Badge>
        )}
      </div>
    </div>

    {uow.dependencies.length > 0 && (
      <div className="mt-1.5 flex items-center gap-1.5 pl-0.5">
        <DependencyArrowIcon />
        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
          depends on
        </span>
        {uow.dependencies.map((depId) => (
          <Badge key={depId} variant="info" size="sm">
            {depId}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

// ---- Tracker summary ----

const TrackerSummary: FC<{
  tracker: NonNullable<PlanRevisionTelemetry["tracker"]>;
}> = ({ tracker }) => {
  const createdCount = tracker.created.filter(
    (c) => c.status === "created",
  ).length;
  const transitionedCount = tracker.transitions.filter((t) => t.changed).length;
  const linkedCount = tracker.dependencies.reduce(
    (sum, d) => sum + d.linked.length,
    0,
  );

  if (createdCount === 0 && transitionedCount === 0 && linkedCount === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {createdCount > 0 && (
        <Badge variant="success" size="sm">
          {createdCount} ticket{createdCount !== 1 ? "s" : ""} created
        </Badge>
      )}
      {transitionedCount > 0 && (
        <Badge variant="info" size="sm">
          {transitionedCount} transition{transitionedCount !== 1 ? "s" : ""}
        </Badge>
      )}
      {linkedCount > 0 && (
        <Badge variant="default" size="sm">
          {linkedCount} link{linkedCount !== 1 ? "s" : ""}
        </Badge>
      )}
    </div>
  );
};
